import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Alert, Box, Button, Chip, CircularProgress, IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tooltip, Typography, useMediaQuery,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import apiService from '../services/api.service';
import { showSnackbar } from '../store/slices/uiSlice';
import { SoaConfig, SoaTemplateSummary } from '../types/formattedSoa';
import { soaErrorMessage } from '../utils/formattedSoa';
import SoaTemplateDesigner from '../components/formattedSoa/TemplateDesigner';
import { APPLIES_LABELS, ConfirmDialog, CopyTemplateDialog, NewTemplateDialog } from '../components/formattedSoa/TemplateDialogs';

/**
 * Sales Portal > Statement Templates (module formatted-soa-designer; docs/FORMATTED_SOA.md): the selected
 * company's Formatted SOA templates, and the designer for one of them (/sales/formatted-soa/templates/:id).
 */

const LIST_PATH = '/sales/formatted-soa/templates';

const TemplateList: React.FC<{ companyId: number }> = ({ companyId }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const notify = useCallback(
    (message: string, severity: 'success' | 'error' | 'info' | 'warning' = 'success') => dispatch(showSnackbar({ message, severity })),
    [dispatch],
  );
  const narrow = useMediaQuery('(max-width:899px)');
  const [config, setConfig] = useState<SoaConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [copying, setCopying] = useState<SoaTemplateSummary | null>(null);
  const [deleting, setDeleting] = useState<SoaTemplateSummary | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await apiService.getSoaConfig(companyId);
      setConfig(res.data as SoaConfig);
      setError(null);
    } catch (e: any) {
      setError(soaErrorMessage(e, 'The templates could not be loaded'));
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { void load(); }, [load]);

  const run = async (t: SoaTemplateSummary, action: () => Promise<unknown>, done: string) => {
    setBusy(t.templateId);
    try {
      await action();
      notify(done);
      await load();
    } catch (e: any) {
      notify(soaErrorMessage(e, 'That did not work'), 'error');
    } finally {
      setBusy(null);
    }
  };

  const templates = config?.templates ?? [];
  const canDesign = !!config?.permissions.design;

  return (
    <Box component="section" aria-labelledby="soa-templates-heading" sx={{ p: { xs: 1.5, sm: 2 } }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1} sx={{ mb: 2 }}>
        <Box>
          <Typography id="soa-templates-heading" variant="h5" component="h1">Statement Templates</Typography>
          <Typography variant="body2" color="text.secondary">
            Layouts of the Formatted SOA for {config?.companyName || 'this company'}: fields, positions, fonts, colours, borders,
            headers, footers, totals and page numbers. The default one is chosen first when printing.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<ReceiptLongIcon />} onClick={() => navigate('/sales/formatted-soa')}>Print statements</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)} disabled={!canDesign}>New template</Button>
        </Stack>
      </Stack>

      {narrow && <Alert severity="info" sx={{ mb: 2 }}>The designer needs a wider screen (900 px or more); the list works here.</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={() => { void load(); }}>Retry</Button>}>{error}</Alert>}
      {config && !canDesign && (
        <Alert severity="info" sx={{ mb: 2 }}>You can use these templates; changing them needs the Statement Templates module (User Management).</Alert>
      )}

      <Paper variant="outlined">
        <TableContainer>
          <Table size="small" aria-label="Statement templates">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>For</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Description</TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Last saved</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && !config && (
                <TableRow><TableCell colSpan={5} align="center"><CircularProgress size={24} /></TableCell></TableRow>
              )}
              {templates.map((t) => (
                <TableRow key={t.templateId} hover>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography
                        component="button"
                        onClick={() => navigate(`${LIST_PATH}/${t.templateId}`)}
                        disabled={!canDesign}
                        sx={{ border: 0, background: 'none', p: 0, font: 'inherit', fontWeight: 600, color: canDesign ? 'primary.main' : 'text.primary', cursor: canDesign ? 'pointer' : 'default', textAlign: 'left' }}
                      >
                        {t.name}
                      </Typography>
                      {t.isDefault && <Chip size="small" color="primary" variant="outlined" label="Default" />}
                    </Stack>
                  </TableCell>
                  <TableCell>{APPLIES_LABELS[t.appliesTo]}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, maxWidth: 360 }}>
                    <Typography variant="body2" color="text.secondary" noWrap title={t.description}>{t.description || '—'}</Typography>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, whiteSpace: 'nowrap' }}>
                    {t.updatedDate ? new Date(t.updatedDate).toLocaleString() : ''}{t.updatedByName ? ` · ${t.updatedByName}` : ''}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {busy === t.templateId ? <CircularProgress size={20} /> : (
                      <>
                        <Tooltip title="Open in the designer"><span>
                          <IconButton size="small" aria-label={`Edit ${t.name}`} disabled={!canDesign} onClick={() => navigate(`${LIST_PATH}/${t.templateId}`)}><EditIcon fontSize="small" /></IconButton>
                        </span></Tooltip>
                        <Tooltip title="Duplicate in this company"><span>
                          <IconButton size="small" aria-label={`Duplicate ${t.name}`} disabled={!canDesign}
                            onClick={() => run(t, () => apiService.copySoaTemplate(t.templateId, [companyId]), `“${t.name}” duplicated`)}>
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </span></Tooltip>
                        <Tooltip title="Copy to other companies"><span>
                          <IconButton size="small" aria-label={`Copy ${t.name} to other companies`} disabled={!canDesign} onClick={() => setCopying(t)}><DriveFileMoveIcon fontSize="small" /></IconButton>
                        </span></Tooltip>
                        <Tooltip title={t.isDefault ? 'The default template' : 'Make it the default'}><span>
                          <IconButton size="small" aria-label={t.isDefault ? `${t.name} is the default` : `Make ${t.name} the default`} disabled={!canDesign || t.isDefault}
                            onClick={() => run(t, () => apiService.setDefaultSoaTemplate(t.templateId), `“${t.name}” is now the default`)}>
                            {t.isDefault ? <StarIcon fontSize="small" color="primary" /> : <StarBorderIcon fontSize="small" />}
                          </IconButton>
                        </span></Tooltip>
                        <Tooltip title="Delete"><span>
                          <IconButton size="small" aria-label={`Delete ${t.name}`} color="error" disabled={!canDesign} onClick={() => setDeleting(t)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                        </span></Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {config && !templates.length && (
                <TableRow><TableCell colSpan={5}><Typography variant="body2" color="text.secondary">No template yet.</Typography></TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <NewTemplateDialog
        open={creating}
        companyId={companyId}
        templates={templates}
        onClose={() => setCreating(false)}
        onCreated={(id) => { setCreating(false); navigate(`${LIST_PATH}/${id}`); }}
      />
      <CopyTemplateDialog
        open={!!copying}
        template={copying}
        currentCompanyId={companyId}
        onClose={() => setCopying(null)}
        onCopied={(created) => {
          setCopying(null);
          notify(created.length === 1 ? `Copied as “${created[0].name}”` : `Copied to ${created.length} companies`);
          void load();
        }}
      />
      <ConfirmDialog
        open={!!deleting}
        title="Delete the template?"
        text={<>“{deleting?.name}” will no longer be offered for printing.{deleting?.isDefault ? ' Another template becomes the default.' : ''}</>}
        confirmLabel="Delete"
        danger
        busy={!!deleting && busy === deleting.templateId}
        onClose={() => setDeleting(null)}
        onConfirm={() => {
          const t = deleting;
          if (!t) return;
          void run(t, () => apiService.deleteSoaTemplate(t.templateId), `“${t.name}” deleted`).then(() => setDeleting(null));
        }}
      />
    </Box>
  );
};

const SoaTemplates: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const companyId = useMemo(() => Number(localStorage.getItem('selectedCompanyId') || '') || 0, []);
  if (!companyId) return <Alert severity="info" sx={{ m: 2 }}>Select a company first.</Alert>;
  if (id !== undefined) {
    const templateId = Number(id);
    if (!Number.isInteger(templateId) || templateId <= 0) return <Alert severity="error" sx={{ m: 2 }}>This template address is not valid.</Alert>;
    return <SoaTemplateDesigner templateId={templateId} companyId={companyId} />;
  }
  return <TemplateList companyId={companyId} />;
};

export default SoaTemplates;
